"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface HoraDef {
  id: string;
  name: string;
  hindi: string;
  planet: string;
  symbol: string;
  nature: "Most Auspicious" | "Auspicious" | "Neutral / Specific" | "Caution Required";
  bestFor: string[];
  avoidFor: string;
  mantra: string;
}

const HORAS: HoraDef[] = [
  {
    id: "guru",
    name: "Jupiter Hora",
    hindi: "गुरु होरा",
    planet: "Jupiter (Guru 🟡)",
    symbol: "✨",
    nature: "Most Auspicious",
    bestFor: [
      "Initiating new ventures & business inaugurations",
      "Higher education & religious ceremonies",
      "Financial investments & opening accounts",
      "Meeting mentors, teachers & advisors",
    ],
    avoidFor: "Avoid unethical or aggressive confrontations during Guru hora.",
    mantra: "Om Gram Greem Groum Sah Gurave Namah",
  },
  {
    id: "shukra",
    name: "Venus Hora",
    hindi: "शुक्र होरा",
    planet: "Venus (Shukra ⚪)",
    symbol: "💎",
    nature: "Auspicious",
    bestFor: [
      "Purchasing jewelry, luxury garments & vehicles",
      "Marriage proposals, romance & social events",
      "Artistic, creative & entertainment projects",
      "Financial settlements and partnerships",
    ],
    avoidFor: "Avoid hostile arguments or surgical procedures during Shukra hora.",
    mantra: "Om Dram Dreem Droum Sah Shukraya Namah",
  },
  {
    id: "budh",
    name: "Mercury Hora",
    hindi: "बुध होरा",
    planet: "Mercury (Budh 🟢)",
    symbol: "📜",
    nature: "Auspicious",
    bestFor: [
      "Signing contracts, agreements & documentation",
      "Trading, commerce, sales & marketing campaigns",
      "Coding, software development & research",
      "Academic exams and learning new subjects",
    ],
    avoidFor: "Avoid risky speculation when Mercury is retrograde.",
    mantra: "Om Bram Breem Broum Sah Budhaya Namah",
  },
  {
    id: "surya",
    name: "Sun Hora",
    hindi: "सूर्य होरा",
    planet: "Sun (Surya ☀️)",
    symbol: "☀️",
    nature: "Auspicious",
    bestFor: [
      "Government applications, tenders & executive meetings",
      "Taking leadership decisions and political affairs",
      "Purchasing precious gold or copper items",
      "Spiritual Surya Arghya and Vedic prayers",
    ],
    avoidFor: "Avoid travel and soft emotional discussions during Surya hora.",
    mantra: "Om Hram Hreem Hroum Sah Suryaya Namah",
  },
  {
    id: "chandra",
    name: "Moon Hora",
    hindi: "चंद्र होरा",
    planet: "Moon (Chandra 🌙)",
    symbol: "🌙",
    nature: "Auspicious",
    bestFor: [
      "Travel, journeys and water-related activities",
      "Domestic shopping and family gatherings",
      "Public relations and community outreach",
      "Meditation, yoga and emotional grounding",
    ],
    avoidFor: "Avoid signing permanent binding contracts during waning Moon.",
    mantra: "Om Shram Shreem Shroum Sah Chandraya Namah",
  },
  {
    id: "mangal",
    name: "Mars Hora",
    hindi: "मंगल होरा",
    planet: "Mars (Mangal 🔴)",
    symbol: "⚔️",
    nature: "Neutral / Specific",
    bestFor: [
      "Sports, workouts and physical defense training",
      "Purchasing real estate or agricultural land",
      "Resolving legal disputes and technical engineering",
      "Administrative disciplinary actions",
    ],
    avoidFor: "Strictly avoid marriage negotiations or peaceful reconciliations.",
    mantra: "Om Kram Kreem Kroum Sah Bhaumaya Namah",
  },
  {
    id: "shani",
    name: "Saturn Hora",
    hindi: "शनि होरा",
    planet: "Saturn (Shani 🪐)",
    symbol: "🪐",
    nature: "Caution Required",
    bestFor: [
      "Laying building foundations and construction work",
      "Dealing in oil, iron, machinery and scrap",
      "Long-term hard discipline, labor and austerity",
      "Planting trees and agriculture",
    ],
    avoidFor: "Avoid starting auspicious ceremonies, weddings, or medical surgeries.",
    mantra: "Om Pram Preem Proum Sah Shanaischaraya Namah",
  },
];

export default function HoraRadarStudio() {
  const [selectedId, setSelectedId] = useState<string>("guru");

  const hora = useMemo<HoraDef>(() => {
    return HORAS.find((h) => h.id === selectedId) || HORAS[0];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Which is the best Shubh Hora today for my activity according to Aaj Ka Panchang? Please analyze the planetary hour timing for my city.`;
    try {
      safeSessionSet("seo_entry_intent", "panchang_lookup");
      safeSessionSet("seo_entry_path", "/panchang");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <HoraSelector selectedId={selectedId} onSelect={setSelectedId} />
      <HoraDetailCard hora={hora} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Timing Shastra
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Aaj Ka Shubh Hora &amp; Auspicious Timings (आज की शुभ होरा)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check the 7 Planetary Horas of the day to identify auspicious hours for business, finance,
        career, travel, and religious rituals.
      </p>
    </div>
  );
}

function HoraSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {HORAS.map((h) => {
          const isSelected = h.id === selectedId;
          return (
            <button
              key={h.id}
              type="button"
              onClick={() => onSelect(h.id)}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="text-lg block mb-1">{h.symbol}</span>
              <span className="block text-caption font-bold text-text-primary">
                {h.name.split(" ")[0]}
              </span>
              <span className="block text-caption text-text-muted">{h.hindi}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HoraDetailCard({ hora, onConsult }: { hora: HoraDef; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/panchang"), "/panchang", "en");

  const badgeColor =
    hora.nature === "Most Auspicious"
      ? "bg-success/10 text-success"
      : hora.nature === "Auspicious"
        ? "bg-intent-brand/10 text-intent-brand"
        : hora.nature === "Neutral / Specific"
          ? "bg-warning/10 text-warning"
          : "bg-surface-raised text-text-muted";

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {hora.symbol} {hora.name} ({hora.hindi})
            </h3>
            <span className={`px-3 py-0.5 rounded-full text-caption font-bold ${badgeColor}`}>
              {hora.nature}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Governed by: <strong className="text-text-primary">{hora.planet}</strong> &middot; Order
            follows the ancient Chaldean sequence.
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check Today&apos;s Hora Timing ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-2">
            Highly Favorable Activities (शुरू करने योग्य कार्य)
          </p>
          <ul className="space-y-1.5 text-caption text-text-primary">
            {hora.bestFor.map((item, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-success">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
          <p className="text-caption font-bold uppercase tracking-wider text-warning mb-2">
            What to Avoid (सावधानी)
          </p>
          <p className="text-caption text-text-primary leading-relaxed mb-4">{hora.avoidFor}</p>
          <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
            Governing Beej Mantra
          </p>
          <p className="text-caption font-serif italic text-text-primary">
            &quot;{hora.mantra}&quot;
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Need exact sunrise and sunset based Choghadiya and Rahu Kaal timings?
        </span>
        <Link
          href="/choghadiya/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Open Aaj Ka Choghadiya →
        </Link>
      </div>
    </div>
  );
}
